import { z } from 'zod';
import { networkBuild } from '@hoodmorning/config';
export * from './launchpad.js';
export * from './launchpadEvents.js';
export * from './conditionalOrderHistory.js';
export * from './externalOrderHistory.js';
export * from './engineRecovery.js';
export * from './productCapabilities.js';
export * from './productAnalytics.js';

// Existing trade/order/evidence artifacts remain the archived4663 contract until
// their coherent conversion packets. Account onboarding selects exactly one
// build identity; this is not permission to consume either network's artifacts.
export const ROBINHOOD_CHAIN_ID = 4663 as const;
export const SELECTED_ACCOUNT_CHAIN_ID = networkBuild.chainId;
export const addressSchema = z.string().regex(/^0x[a-fA-F0-9]{40}$/);
export const hexSchema = z.string().regex(/^0x(?:[a-fA-F0-9]{2})*$/);
export const baseUnitStringSchema = z.string().regex(/^(0|[1-9][0-9]*)$/);

export type Address = `0x${string}`;
export type Hex = `0x${string}`;
export type BaseUnitString = `${bigint}`;

export const signerKindSchema = z.enum(['privy-embedded', 'external-eip1193']);
export type SignerKind = z.infer<typeof signerKindSchema>;

export const accountRegistryRecordSchema = z.object({
  schemaVersion: z.literal(1),
  userId: z.string().min(1),
  chainId: z.literal(SELECTED_ACCOUNT_CHAIN_ID),
  vaultIndex: z.number().int().nonnegative(),
  vaultAddress: addressSchema,
  controllingSigner: addressSchema,
  signerKind: signerKindSchema,
  accountType: z.literal('mav2-sma-b'),
  entryPoint: addressSchema,
  factory: addressSchema,
  implementation: addressSchema,
  salt: hexSchema,
  deployedAtTx: hexSchema.nullable(),
  configVersion: z.string().min(1),
  status: z.enum(['counterfactual', 'deployed', 'rotation-pending', 'active']),
});
export type AccountRegistryRecord = z.infer<typeof accountRegistryRecordSchema>;

export const tradeIntentSchema = z.object({
  schemaVersion: z.literal(1),
  intentId: z.string().uuid(),
  userId: z.string().min(1),
  vault: addressSchema,
  chainId: z.literal(ROBINHOOD_CHAIN_ID),
  side: z.enum(['BUY', 'SELL']),
  type: z.literal('EXACT_INPUT'),
  tokenIn: z.union([addressSchema, z.literal('NATIVE')]),
  tokenOut: z.union([addressSchema, z.literal('NATIVE')]),
  amountIn: baseUnitStringSchema,
  slippageBps: z.number().int().min(1).max(500),
  referralSnapshotId: z.string().uuid().nullable(),
  feePolicyVersion: z.string().min(1),
  expiresAt: z.iso.datetime(),
});
// Archived4663 terms keep their exact schema and representation. Reusable
// lower-level ports may retain a caller's literal network, but this type is not
// a parser or permission to execute either network in one running bundle.
export type TradeIntent<ChainId extends 4663 | 46630 = 4663> =
  Omit<z.infer<typeof tradeIntentSchema>, 'chainId'> & { chainId: ChainId };
export const selectedTradeIntentSchema = tradeIntentSchema.extend({ chainId: z.literal(SELECTED_ACCOUNT_CHAIN_ID) });
export type SelectedTradeIntent = z.infer<typeof selectedTradeIntentSchema>;

export const tradeQuoteSchema = z.object({
  schemaVersion: z.literal(1),
  quoteId: z.string().uuid(),
  intentId: z.string().uuid(),
  route: z.literal('CLASSIC'),
  amountIn: baseUnitStringSchema,
  expectedAmountOut: baseUnitStringSchema,
  minimumAmountOut: baseUnitStringSchema,
  hoodMorningFeeBps: z.union([z.literal(50), z.literal(45)]),
  hoodMorningFeeAmount: baseUnitStringSchema,
  feeRecipient: addressSchema,
  gasEstimate: baseUnitStringSchema,
  priceImpactBps: z.number().int().nullable(),
  router: addressSchema,
  // Shape only; matching server/profile decoder and runtime pins are mandatory
  // at semantic admission. Legacy stored-record codecs retain their literal.
  routerVersion: z.enum(['2.1.1', '2.1.2']),
  providerRequestId: z.string().min(1),
  expiresAt: z.iso.datetime(),
});
export type TradeQuote = z.infer<typeof tradeQuoteSchema>;

export const evidenceEventSchema = z.object({
  schemaVersion: z.literal(1),
  eventId: z.string().uuid(),
  type: z.string().min(1),
  aggregateType: z.enum(['identity', 'vault', 'intent', 'transaction', 'referral', 'points']),
  aggregateId: z.string().min(1),
  sequence: z.number().int().nonnegative(),
  idempotencyKey: z.string().min(1),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  actor: z.enum(['user-owner', 'hoodmorning-api', 'indexer', 'operator-correction']),
  chainId: z.literal(ROBINHOOD_CHAIN_ID).nullable(),
  sourceBlockNumber: baseUnitStringSchema.nullable(),
  sourceBlockHash: hexSchema.nullable(),
  confirmation: z.enum(['offchain', 'pending', 'confirmed', 'reorged', 'final']),
  causationId: z.string().nullable(),
  correlationId: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
});
export type EvidenceEvent = z.infer<typeof evidenceEventSchema>;

export interface SignerAdapter {
  readonly kind: SignerKind;
  getOwnerAddress(): Promise<Address>;
  signMessage(message: Uint8Array): Promise<Hex>;
  signTypedData(typedData: unknown): Promise<Hex>;
}

export type VaultIdentity<ChainId extends 4663 | 46630 = 4663> = {
  userId: string;
  chainId: ChainId;
  vaultIndex: number;
  owner: Address;
};

export type AccountState = {
  address: Address;
  owner: Address;
  deployed: boolean;
  unexpectedModules: Address[];
};

export type PreparedCall = { to: Address; value: bigint; data: Hex };
export type PreparedOperation = {
  operationId: string;
  vault: Address;
  nonceKey: bigint;
  calls: PreparedCall[];
  callsHash: Hex;
  validUntil: string;
};

export interface AccountAdapter {
  predictDefaultVault(input: VaultIdentity): Promise<Address>;
  getState(vault: Address): Promise<AccountState>;
  prepareBatch(vault: Address, calls: PreparedCall[], nonceKey: bigint): Promise<PreparedOperation>;
  signAndSend(operation: PreparedOperation, signer: SignerAdapter): Promise<{ userOperationHash: Hex }>;
}

export interface ChainAdapter {
  simulate(operation: PreparedOperation): Promise<{ success: boolean; reason?: string }>;
  getReceipt(reference: Hex): Promise<{ transactionHash: Hex; blockNumber: bigint; blockHash: Hex; success: boolean } | null>;
  verifyPinnedCode(): Promise<{ valid: boolean; failures: string[] }>;
}

export interface VenueAdapter {
  quote(intent: TradeIntent): Promise<TradeQuote>;
  prepare(quote: TradeQuote): Promise<PreparedCall[]>;
  validate(calls: PreparedCall[], intent: TradeIntent, quote: TradeQuote): Promise<{ valid: boolean; failures: string[] }>;
}

export function feeBpsForReferral(hasReferral: boolean): 50 | 45 {
  return hasReferral ? 45 : 50;
}

export function feeAmount(amount: bigint, bps: 50 | 45): bigint {
  if (amount < 0n) throw new RangeError('amount must be non-negative');
  return (amount * BigInt(bps)) / 10_000n;
}

export function referrerShare(collectedFee: bigint): bigint {
  if (collectedFee < 0n) throw new RangeError('collectedFee must be non-negative');
  return (collectedFee * 2_000n) / 10_000n;
}
export * from './canonicalSwapEvents.js';
export * from './ownLaunchTrades.js';
